import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, XCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  count: 8,
  items: [
    { id: '1', userName: 'Tom Wilson', userId: '1', requirementName: 'Asbestos Awareness', expiredDays: 15 },
    { id: '2', userName: 'Lisa Davis', userId: '2', requirementName: 'PASMA', expiredDays: 8 },
    { id: '3', userName: 'James Taylor', userId: '3', requirementName: 'Confined Spaces', expiredDays: 3 },
    { id: '4', userName: 'Sarah Brown', userId: '4', requirementName: 'Fire Safety', expiredDays: 22 },
    { id: '5', userName: 'Mike Johnson', userId: '5', requirementName: 'First Aid', expiredDays: 45 },
  ],
};

export function ExpiredRequirementsTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];

        // First, get all user IDs for this company
        let userIdsToQuery = [];

        if (filter?.spaceId) {
          // Get team members for this specific space
          const { data: teamMembers } = await supabase
            .from('team_members')
            .select('user_id')
            .eq('project_id', filter.spaceId);

          userIdsToQuery = (teamMembers || []).map(tm => tm.user_id);
        } else {
          // Get all users in the company
          const { data: companyUsers } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('company_id', company.id);

          userIdsToQuery = (companyUsers || []).map(u => u.id);
        }

        if (userIdsToQuery.length === 0) {
          setData({ count: 0, items: [] });
          setLoading(false);
          return;
        }

        // Query expired qualifications
        const { data: qualifications, count, error } = await supabase
          .from('user_qualifications')
          .select(`
            id,
            expiry_date,
            user_id,
            requirement:compliance_requirements(id, name)
          `, { count: 'exact' })
          .in('user_id', userIdsToQuery)
          .not('expiry_date', 'is', null)
          .lt('expiry_date', today)
          .order('expiry_date', { ascending: true })
          .limit(10);

        if (error) throw error;

        // Get unique user IDs from qualifications
        const qualUserIds = [...new Set((qualifications || []).map(q => q.user_id))];

        // Fetch user profiles for these users
        let userProfiles = [];
        if (qualUserIds.length > 0) {
          const { data } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name')
            .in('id', qualUserIds);
          userProfiles = data || [];
        }

        // Create a lookup map
        const userMap = userProfiles.reduce((acc, u) => {
          acc[u.id] = u;
          return acc;
        }, {});

        const items = (qualifications || []).map(q => {
          const user = userMap[q.user_id] || {};
          return {
            id: q.id,
            userName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
            userId: q.user_id,
            requirementName: q.requirement?.name || 'Unknown',
            expiredDays: Math.ceil((new Date() - new Date(q.expiry_date)) / (1000 * 60 * 60 * 24)),
          };
        });

        setData({ count: count || 0, items });
      } catch (err) {
        console.error('Failed to load expired data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 150;
  const isTall = height > width * 1.2;
  const isLarge = width >= 350 && height >= 300;

  // Calculate how many items to show based on available height
  const getVisibleCount = () => {
    if (isXS) return 0;
    if (isSmall) return 2;
    const itemHeight = 48;
    const headerHeight = isLarge ? 50 : 40;
    const availableHeight = height - headerHeight - 20;
    return Math.max(1, Math.floor(availableHeight / itemHeight));
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-3 w-16 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  // All clear state
  if (data.count === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-center overflow-visible">
        <div className="bg-green-100 p-2.5 rounded-full mb-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
        <p className="text-sm font-medium text-green-700">All Clear</p>
        {!isXS && <p className="text-xs text-gray-500">No expired</p>}
      </div>
    );
  }

  const visibleItems = data.items.slice(0, getVisibleCount());
  const remainingCount = data.count - visibleItems.length;

  // XS Layout: Just count with alert icon
  if (isXS) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className="text-3xl font-bold text-red-600 leading-none">
            {data.count}
          </div>
          <p className="text-xs text-red-500 mt-1">expired</p>
        </div>
      </div>
    );
  }

  // Small Layout: Count with icon
  if (isSmall) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-center p-2 overflow-visible">
        <div className="bg-red-100 p-2 rounded-full mb-1.5">
          <AlertCircle className="h-5 w-5 text-red-600" />
        </div>
        <p className="text-2xl font-bold text-red-600">{data.count}</p>
        <p className="text-xs text-gray-600">expired</p>
      </div>
    );
  }

  // Large Layout: Full list with header
  if (isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="font-medium">Expired</span>
          </div>
          <Badge variant="destructive">{data.count} total</Badge>
        </div>

        {/* List */}
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {visibleItems.map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(`/profile/${item.userId}`)}
              className="flex items-center justify-between p-2 rounded-lg bg-red-50 hover:bg-red-100 cursor-pointer transition-colors border border-red-100"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{item.requirementName}</p>
                <p className="text-xs text-gray-500 truncate">{item.userName}</p>
              </div>
              <span className="text-xs font-medium text-red-600 shrink-0 ml-2">
                {item.expiredDays}d ago
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        {remainingCount > 0 && (
          <button
            onClick={() => navigate('/qualifications')}
            className="mt-2 pt-2 border-t text-center text-sm text-red-600 hover:text-red-700"
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      {/* Header badge */}
      <div className="flex items-center justify-between mb-2">
        <Badge variant="destructive" className="text-sm">
          {data.count} Expired
        </Badge>
      </div>

      {/* List */}
      <div className="flex-1 space-y-1 overflow-hidden">
        {visibleItems.map((item) => (
          <div
            key={item.id}
            onClick={() => navigate(`/profile/${item.userId}`)}
            className="flex items-center justify-between p-1.5 rounded-lg hover:bg-red-50 cursor-pointer transition-colors border border-red-100"
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate">{item.requirementName}</p>
              <p className="text-[10px] text-gray-500 truncate">{item.userName}</p>
            </div>
            <span className="text-[10px] text-red-600 shrink-0 ml-1">
              {item.expiredDays}d
            </span>
          </div>
        ))}
      </div>

      {/* More link */}
      {remainingCount > 0 && (
        <button
          onClick={() => navigate('/qualifications')}
          className="text-xs text-red-600 hover:text-red-700 pt-1"
        >
          +{remainingCount} more
        </button>
      )}
    </div>
  );
}
